import type { ReactElement } from 'react';

import {
  RiCodeLine,
  RiLoopRightAiLine,
  RiRadarLine,
  RiRobot2Line,
  RiTreasureMapLine,
} from '@remixicon/react';

import { ButtonIcon, Pressable, Text } from '@spotify-internal/encore-web';
import {
  Popover,
  PopoverRoot,
  Tooltip,
  TooltipRoot,
} from '@spotify-internal/encore-web/next';
import { ObservationsV1IssueAutonomyLevel } from '@spotify/confidence-graphql';

import { getAutonomyLevelLabel } from '../../types';
import { IssueAutonomyLevelPopoverContent } from './IssueAutonomyLevelPopoverContent';

import styles from './IssueAutonomyLevelPicker.module.css';
import popoverStyles from './IssueAutonomyLevelPopoverContent.module.css';

export type IssueAutonomyLevelPickerProps = {
  value?: ObservationsV1IssueAutonomyLevel | null;
  disabled?: boolean;
  onChange: (level: ObservationsV1IssueAutonomyLevel) => void;
};

const getAutonomyLevelIcon = (
  level?: ObservationsV1IssueAutonomyLevel | null,
): ReactElement => {
  switch (level) {
    case ObservationsV1IssueAutonomyLevel.Collect:
      return <RiRadarLine size={16} aria-hidden />;
    case ObservationsV1IssueAutonomyLevel.Investigate:
      return <RiTreasureMapLine size={16} aria-hidden />;
    case ObservationsV1IssueAutonomyLevel.Build:
      return <RiCodeLine size={16} aria-hidden />;
    case ObservationsV1IssueAutonomyLevel.Full:
      return <RiLoopRightAiLine size={16} aria-hidden />;
    default:
      return <RiRobot2Line size={16} aria-hidden />;
  }
};

export const IssueAutonomyLevelPicker = ({
  value,
  disabled = false,
  onChange,
}: IssueAutonomyLevelPickerProps) => {
  const autonomyLevelLabel = value ? getAutonomyLevelLabel(value) : 'Not set';
  const trigger = (
    <button
      type="button"
      className={styles.trigger}
      aria-label={`Change autonomy, current value: ${autonomyLevelLabel}`}
      disabled={disabled}
      data-track-id="button.autonomy-level"
      data-track-action="open-menu"
     data-track-source-anchor="a27cbbed7e;issue-autonomy-level-picker-button">
      {getAutonomyLevelIcon(value)}
      <Text as="span" variant="bodySmall">
        {autonomyLevelLabel}
      </Text>
    </button>
  );

  if (disabled) {
    return trigger;
  }

  return (
    <PopoverRoot>
      <Pressable>{trigger}</Pressable>
      <Popover
        aria-label="Change autonomy"
        placement="bottomStart"
        hasNoArrow
        className={popoverStyles.popover}
      >
        {({ close }) => (
          <IssueAutonomyLevelPopoverContent
            value={value}
            disabled={disabled}
            onChange={onChange}
            onSelected={close}
           data-track-source-anchor="f373d7db0b;issue-autonomy-level-picker-issue-autonomy-level"/>
        )}
      </Popover>
    </PopoverRoot>
  );
};

export type IssueAutonomyLevelButtonProps = IssueAutonomyLevelPickerProps;

export const IssueAutonomyLevelButton = ({
  value,
  disabled = false,
  onChange,
}: IssueAutonomyLevelButtonProps) => {
  const autonomyLevelLabel = value ? getAutonomyLevelLabel(value) : 'Not set';
  const ariaLabel = `Change autonomy, current value: ${autonomyLevelLabel}`;
  const trigger = (
    <ButtonIcon
      variant="tertiary"
      size="small"
      aria-label={ariaLabel}
      disabled={disabled}
      data-track-id="button.autonomy-level"
      data-track-action="open-menu"
    >
      {getAutonomyLevelIcon(value)}
    </ButtonIcon>
  );

  if (disabled) {
    return trigger;
  }

  return (
    <PopoverRoot>
      <TooltipRoot>
        <Pressable>{trigger}</Pressable>
        <Tooltip>Autonomy level</Tooltip>
      </TooltipRoot>
      <Popover
        aria-label="Change autonomy"
        placement="bottomEnd"
        hasNoArrow
        className={popoverStyles.popover}
      >
        {({ close }) => (
          <IssueAutonomyLevelPopoverContent
            value={value}
            disabled={disabled}
            onChange={onChange}
            onSelected={close}
           data-track-source-anchor="afa80c950c;issue-autonomy-level-button-issue-autonomy-level"/>
        )}
      </Popover>
    </PopoverRoot>
  );
};
